import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { BusyButton } from "../../components/etams/BusyButton";
import { toast } from "sonner";
import { catatKmAset, apiErrorMessage } from "../../lib/asetApi";
import { label } from "../../hooks/useAsetMeta";
import { formatDate } from "../../lib/format";
import { formatKm, cariKaryawanRegion, ambilKaryawan } from "./bantuAset";
import PemilihCari from "../../components/etams/PemilihCari";
import { labelKaryawan } from "../../lib/sumberPilihan";

// Form Pembacaan Km (4b-2) — tab "Pembacaan Km" Detail Aset, kendaraan saja.
// Cermin `aset_service.catat_km` (R11) + RPC `aset_catat_pembacaan_km`:
//   * sumber MANUAL: PERAN_CATAT region + PIC ber-akun; KOREKSI: hanya KC/OWNER
//     (403 KOREKSI_HANYA_KC_OWNER) — pilihan KOREKSI hanya tampil bila `bolehKoreksi`.
//   * KOREKSI wajib km_odometer_lama + catatan (CHECK aset_km_koreksi_konsisten).
//   * tanggal_baca DIISI RPC menurut zona region — body `KmBaru` nol menerimanya,
//     jadi di layar ia keterangan baca-saja (`hariIni` dari view/backend), bukan isian.
//   * Trigger aset_km_monoton: KM_MUNDUR / TANGGAL_BACA_MUNDUR (kecuali KOREKSI)
//     → 400 backend, ditampilkan verbatim.
//   * pembaca_employee_id: karyawan region aset; kosong → backend memakai PIC aset.

const KOSONG = { km: "", sumber: "MANUAL", pembaca_employee_id: "", km_odometer_lama: "", catatan: "" };

export default function KmFormDialog({ terbuka, onTutup, onSelesai, meta, aset, kmTerakhir, hariIni, bolehKoreksi }) {
  const [form, setForm] = useState(KOSONG);
  const [menyimpan, setMenyimpan] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (terbuka) setForm({ ...KOSONG, pembaca_employee_id: aset?.pic_employee_id || "", km_odometer_lama: kmTerakhir ?? "" });
  }, [terbuka, aset, kmTerakhir]);

  const koreksi = form.sumber === "KOREKSI";
  const simpan = async () => {
    if (form.km === "") { toast.error("Km wajib diisi."); return; }
    if (koreksi && form.km_odometer_lama === "") { toast.error("Km odometer lama wajib diisi untuk koreksi."); return; }
    if (koreksi && !form.catatan.trim()) { toast.error("Catatan wajib diisi untuk koreksi."); return; }
    setMenyimpan(true);
    try {
      const body = { km: Number(form.km), sumber: form.sumber };
      if (form.pembaca_employee_id) body.pembaca_employee_id = form.pembaca_employee_id;
      if (form.catatan.trim()) body.catatan = form.catatan.trim();
      if (koreksi) body.km_odometer_lama = Number(form.km_odometer_lama);
      const r = await catatKmAset(aset.id, body);
      toast.success(`Km ${formatKm(form.km)} tercatat${r.data?.tanggal_baca ? ` (${formatDate(r.data.tanggal_baca)})` : ""}.`);
      onSelesai(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mencatat km."));
    } finally {
      setMenyimpan(false);
    }
  };

  if (!aset) return null;
  return (
    <Dialog open={terbuka} onOpenChange={(o) => { if (!o) onTutup(); }}>
      <DialogContent data-testid="km-form-dialog">
        <DialogHeader><DialogTitle>Catat Pembacaan Km — {aset.kode}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            Odometer terakhir: <span className="text-foreground font-medium tabular-nums">{formatKm(kmTerakhir)}</span>
            {hariIni ? <> · Tanggal baca: <span className="text-foreground">{formatDate(hariIni)}</span> (hari ini, diisi sistem)</> : null}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Km *</Label>
              <Input type="number" min="0" value={form.km} onChange={(e) => upd("km", e.target.value)} data-testid="km-form-km" />
            </div>
            <div>
              <Label>Jenis Pembacaan</Label>
              {bolehKoreksi ? (
                <Select value={form.sumber} onValueChange={(v) => upd("sumber", v)}>
                  <SelectTrigger data-testid="km-form-sumber"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MANUAL">{label(meta, "sumber_km", "MANUAL")}</SelectItem>
                    <SelectItem value="KOREKSI">{label(meta, "sumber_km", "KOREKSI")}</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input value={label(meta, "sumber_km", "MANUAL")} disabled data-testid="km-form-sumber-terkunci" />
              )}
            </div>
          </div>
          {koreksi && (
            <div data-testid="km-form-koreksi">
              <Label>Km Odometer Lama *</Label>
              <Input type="number" min="0" value={form.km_odometer_lama} onChange={(e) => upd("km_odometer_lama", e.target.value)} data-testid="km-form-odometer-lama" />
              <div className="text-xs text-muted-foreground mt-1">Angka odometer yang salah dan digantikan oleh Km di atas. Jatuh tempo kilometer bergeser sebesar selisihnya.</div>
            </div>
          )}
          <div>
            <Label>Pembaca</Label>
            <PemilihCari value={form.pembaca_employee_id || null} onChange={(k) => upd("pembaca_employee_id", k?.id || "")}
                         ambilSatu={ambilKaryawan} cari={cariKaryawanRegion(aset?.region_id)} label={labelKaryawan}
                         tampilAwal bolehKosong placeholder="PIC aset" placeholderCari="Ketik nama atau kode karyawan…"
                         testId="km-form-pembaca" />
          </div>
          <div>
            <Label>Catatan{koreksi ? " *" : ""}</Label>
            <Textarea rows={2} value={form.catatan} onChange={(e) => upd("catatan", e.target.value)} data-testid="km-form-catatan" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onTutup} disabled={menyimpan}>Batal</Button>
          <BusyButton onClick={simpan} busy={menyimpan} busyText="Menyimpan..." data-testid="km-form-simpan">Simpan</BusyButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
